import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { PhotoLightboxComponent } from '@app/patients/patient-tabs/shared-photos/photo-lightbox/photo-lightbox.component';
import { isNullOrUndefined } from '@app/shared/helpers';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, DataSourceRequestState, SortDescriptor } from '@progress/kendo-data-query';
import { MasterOverlayService } from '@services/actionpanel.service';
import { BlobService } from '@services/blob.service';
import { CommunicationsService } from '@services/communications.service';
import { PatientService } from '@services/patient.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

enum FilterRanges {
  Today,
  Yesterday,
  LastThree,
  Week,
  Month,
  YearToDate,
  Picker,
}

@Component({
  selector: 'app-communication-summary',
  templateUrl: './communication-summary.component.html',
  styleUrls: ['./communication-summary.component.less'],
})
export class CommunicationSummaryComponent implements OnInit, OnDestroy {
  @ViewChild('grid') grid;
  @ViewChild('emailFilterToggle') emailToggle: ElementRef;
  @ViewChild('smsFilterToggle') smsToggle: ElementRef;

  patientId: number;
  gridView: GridDataResult;
  gridState: DataSourceRequestState;
  pageable: any;
  loading = false;
  unsub: Subject<void> = new Subject<void>();
  dateRange = new FormGroup({
    start: new FormControl(),
    end: new FormControl(),
  });
  activeDateFilter: FilterRanges;
  activeTypeFilter: string = 'SMS,Email';
  searchValue = '';

  get FilterRanges() {
    return FilterRanges;
  }

  constructor(
    private route: ActivatedRoute,
    private communicationsService: CommunicationsService,
    private patientService: PatientService,
    private router: Router,
    private masterOverlayService: MasterOverlayService,
    private blobService: BlobService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.initDefaultValue();
    this.filterByDate(FilterRanges.Today);
  }

  search(searchValue: string) {
    this.searchValue = searchValue;
    this.gridState.skip = 0;
    this.gridState.take = 50;
    this.getCommunicationData(false);
  }

  clearFilter() {
    this.activeDateFilter = null;
    this.activeTypeFilter = 'SMS,Email';
    this.dateRange.reset();
    this.searchValue = null;
    this.filterByDate(FilterRanges.Today);
  }

  initDefaultValue() {
    this.gridState = {
      skip: 0,
      take: 50,
      sort: [{ dir: 'desc', field: 'date' }],
      filter: {
        logic: 'or',
        filters: [],
      },
    };

    this.gridView = {
      data: [],
      total: 0,
    };
  }

  onEndDateSet(event: MatDatepickerInputEvent<Date>) {
    if (event.value) {
      this.activeDateFilter = FilterRanges.Picker;
      this.gridState.skip = 0;
      this.gridState.take = 50;
      this.getCommunicationData();
    }
  }

  getCommunicationData(loading = true) {
    this.loading = loading;
    const startDate = new Date(this.dateRange.value.start);
    const endDate = new Date(this.dateRange.value.end);
    this.communicationsService
      .getHistory(
        this.gridState,
        new Date(startDate.toDateString()),
        new Date(endDate.toDateString()),
        this.searchValue
      )
      .pipe(takeUntil(this.unsub))
      .subscribe((result) => {
        this.gridView = {
          data: result.data,
          total: result.total || result.data.length,
        };
        this.loading = false;
      });
  }

  filterByType(event) {
    let amalgamatedKey = '';

    var emailToggleIn = this.emailToggle.nativeElement.checked;
    var smsToggleIn = this.smsToggle.nativeElement.checked;

    //console.log(event.srcElement.name);
    if (event.srcElement.name == 'smsFilter') {
      if (!emailToggleIn) {
        this.emailToggle.nativeElement.checked = true;
        emailToggleIn = true;
      }
    } else if (event.srcElement.name == 'emailFilter') {
      if (!smsToggleIn) {
        this.smsToggle.nativeElement.checked = true;
        smsToggleIn = true;
      }
    }

    if (smsToggleIn && emailToggleIn) {
      amalgamatedKey = 'SMS,Email';
    } else if (smsToggleIn) {
      amalgamatedKey = 'SMS';
    } else if (emailToggleIn) {
      amalgamatedKey = 'Email';
    }

    this.activeTypeFilter = amalgamatedKey;
    if (amalgamatedKey == 'SMS,Email') {
      this.gridState.filter = {
        logic: 'or',
        filters: [],
      };
    } else {
      this.gridState.filter = {
        logic: 'or',
        filters: [{ field: 'type', operator: 'eq', value: amalgamatedKey }],
      };
    }
    this.gridState.skip = 0;
    this.getCommunicationData();
  }

  filterByDate(key: FilterRanges) {
    this.activeDateFilter = key;
    const now = new Date();
    this.gridState.skip = 0;
    this.gridState.take = 50;

    switch (key) {
      case FilterRanges.Today:
        this.dateRange.setValue({ start: now, end: now });
        break;

      case FilterRanges.Yesterday:
        const yesterday = new Date(now.setDate(now.getDate() - 1));
        this.dateRange.setValue({ start: yesterday, end: yesterday });
        break;

      case FilterRanges.LastThree:
        const threeDays = new Date(now.setDate(now.getDate() - 3));
        this.dateRange.setValue({ start: threeDays, end: new Date() });
        break;

      case FilterRanges.Week:
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const week = new Date(today.setDate(today.getDate() - today.getDay()));
        this.dateRange.setValue({ start: week, end: new Date() });
        break;

      case FilterRanges.Month:
        const month = new Date(now.getFullYear(), now.getMonth(), 1);
        this.dateRange.setValue({ start: month, end: new Date() });
        break;

      case FilterRanges.YearToDate:
        const ytd = new Date(now.getFullYear(), 0);
        this.dateRange.setValue({ start: ytd, end: new Date() });
        break;
    }
    this.getCommunicationData();
  }

  pageChange(event: PageChangeEvent) {
    this.gridState.skip = event.skip;
    this.getCommunicationData();
  }

  sortChange(sort: SortDescriptor[]) {
    this.gridState.sort = sort;
    this.getCommunicationData();
  }

  filterChange(filter: CompositeFilterDescriptor) {
    this.gridState.filter = filter;
    this.getCommunicationData();
  }

  sortIconClass(column: any) {
    return this.gridState.sort.length &&
      this.gridState.sort[0].field === column.field &&
      this.gridState.sort[0].dir === 'asc'
      ? 'fad fa-sort-up'
      : this.gridState.sort.length &&
        this.gridState.sort[0].field === column.field &&
        this.gridState.sort[0].dir === 'desc'
      ? 'fad fa-sort-down'
      : 'fas fa-sort';
  }

  goToPatient(dataItem) {
    this.patientService.getPatientById(dataItem.patientId).subscribe((pat) => {
      if (!isNullOrUndefined(pat)) {
        this.patientService.patientPanelPatient = pat;
        this.masterOverlayService.masterOverlayState(true);
        this.router.navigate([
          '/management/communication/summary',
          { outlets: { 'action-panel': ['patient', pat.patientId + '_' + 'patientprofiletab'] } },
        ]);
      }
    });
  }

  navigateToPatientInvoice(patientId: number, reason: string) {
    const invoiceId = parseInt(reason);
    this.patientService
      .getPatientById(patientId)
      .pipe(takeUntil(this.unsub))
      .subscribe((patient: any) => {
        this.patientService.patientPanelPatient = patient;
        this.router.navigate([
          '/management/communication/summary',
          {
            outlets: {
              'action-panel': [
                'patient',
                patientId + '_patientprofiletab',
                'patienttabs',
                'patientaccounttab',
                'invoice',
                invoiceId,
              ],
            },
          },
        ]);
      });
  }

  openPhoto(url: string): void {
    this.dialog.open(PhotoLightboxComponent, {
      panelClass: 'lightbox-dialog',
      width: '100%',
      height: '100%',
      maxWidth: '100%',
      data: {
        photoURL: this.applySaS(url),
      },
    });
  }

  applySaS(baseUrl: string): string {
    const readOnlySAS = this.blobService.getReadOnlySAS();
    return baseUrl + readOnlySAS;
  }

  ngOnDestroy() {
    this.unsub.next();
    this.unsub.complete();
  }
}
